
import { checkLogin, logout, unimpersonate } from '@/api'
import types from '@/types'
import UserStorage from '@unified/js-common/lib/user/User'
import Vue from 'vue'
import UserFlags from '@unified/js-common/lib/user/UserFlags'
import { AppRenderer } from './plugins/globals'
import vuetify from '@/plugins/vuetify'
import { UserAccessService } from './features/Access/UserAuth'
import { diagnoseThemeApply, Theme } from '@/types/theme'

const AlertDialog = () => import('@/components/dialogs/AlertDialog.vue')
const ConfirmDialog = () => import('@/components/dialogs/ConfirmDialog.vue')
const TheNavDrawer = () => import('@/components/TheNavDrawer.vue')
const TheToolBar = () => import('@/components/TheToolBar.vue')
const UserMessage = () => import('@/components/UserMessage.vue')
const LoadingImage = () => import('@/components/LoadingImage.vue')
const SalesAgent = () => import('@/components/SalesAgent.vue')
const PlatformSearchHost = () => import('@/components/PlatformSearchHost.vue')
const AppLoader = () => import('@/AppLoader.vue')

function setFavicon(url: string): void {
  // Find the existing favicon element
  let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']")

  // Create a new link element if one does not already exist
  if (!link) {
    link = document.createElement('link')
  }

  // Set the attributes for the link element
  link.type = 'image/x-icon'
  link.rel = 'shortcut icon'
  link.href = url

  // Append the link element to the head if it does not exist
  if (!document.head.contains(link)) {
    document.head.appendChild(link)
  }
}

export default Vue.extend({
  name: 'UIDashboards',
  components: {
    AlertDialog,
    ConfirmDialog,
    TheNavDrawer,
    TheToolBar,
    UserMessage,
    LoadingImage,
    SalesAgent,
    PlatformSearchHost,
  },
  data() {
    return {
      loading: true,
      useSideNav: true,
      allowSalesAgent: false,
      allowPlatformSearch: false,
      isInSalesDashboard: false,
      scrollLockObserver: null as MutationObserver | null,
      previousBodyMarginRight: null as string | null,
    }
  },
  computed: {
    salesAgentEnabled(): boolean {
      return this.allowSalesAgent
    },
  },
  watch: {
    $route(to, from) {
      this.isInSalesDashboard = to.name === 'salesDashboard'
      if (window.pendo && window.pendo.location) {
        window.pendo.location.setUrl(window.location.href)
      }
    },
  },
  async beforeCreate() {
    checkLogin()
  },
  beforeDestroy() {
    const body = document.body
    if (body && this.previousBodyMarginRight !== null) {
      if (this.previousBodyMarginRight) {
        body.style.setProperty('margin-right', this.previousBodyMarginRight)
      } else {
        body.style.removeProperty('margin-right')
      }
      this.previousBodyMarginRight = null
    }

    if (this.scrollLockObserver) {
      this.scrollLockObserver.disconnect()
      this.scrollLockObserver = null
    }
  },
  async created() {
    await this.init()
    this.loading = false
  },
  async mounted() {
    this.setupScrollLockMarginOverride()
    await this.accessChecks()
    if (this.$refs.newNav) {
      const userPlatformNav = `/lib/user-platform/index.js?v=${types.config.userPlatformVersion()}`
      try {
        const navigation: AppRenderer = await window.$loadScript(
          userPlatformNav,
          function ifUserPlatformNavUnified() {
            // Here we have an implicit dependency on loading global.js. For performance purpose, global.js is loaded in App.vue.
            return !!(window.main && window.main.renderApp) && !!window.unifiedGlobal
          },
          'main',
          10000
        )
        const basename = window.location.pathname
        const themeDiagnosis = diagnoseThemeApply()
        // Shell logo is owned by user-platform getUserTheme (separate isIHMLocation: prefix === "ihm" only).
        // eslint-disable-next-line no-console
        console.warn('[Theme] user-platform mount', themeDiagnosis)
        if (themeDiagnosis.shellLogoWarning) {
          // eslint-disable-next-line no-console
          console.warn('[Theme] Campaign shell logo:', themeDiagnosis.shellLogoWarning)
        }
        navigation.renderApp({
          autoPoll: false,
          basename: basename,
          noHeader: true,
          isIHMPlatform: themeDiagnosis.theme === Theme.IHM,
        })
        const mountPoint = document.getElementById('main-app')

        if (mountPoint) {
          let vueHost = document.getElementById('vue-dashboard-host')
          if (!vueHost) {
            vueHost = document.createElement('div')
            vueHost.id = 'vue-dashboard-host'
            mountPoint.appendChild(vueHost)
          }

          const { $router, $store } = this
          new Vue({
            errorCaptured: Vue.config.errorHandler,
            router: $router,
            store: $store,
            vuetify,
            render: (h) => h(AppLoader),
          }).$mount(vueHost)
        }
      } catch (e) {}
    }
  },
  methods: {
    setupScrollLockMarginOverride() {
      const syncBodyMargin = () => {
        const body = document.body
        if (!body) {
          return
        }

        if (body.hasAttribute('data-scroll-locked')) {
          if (this.previousBodyMarginRight === null) {
            this.previousBodyMarginRight = body.style.getPropertyValue('margin-right')
          }
          body.style.setProperty('margin-right', '0px', 'important')
          return
        }

        if (this.previousBodyMarginRight !== null) {
          if (this.previousBodyMarginRight) {
            body.style.setProperty('margin-right', this.previousBodyMarginRight)
          } else {
            body.style.removeProperty('margin-right')
          }
          this.previousBodyMarginRight = null
        }
      }

      syncBodyMargin()
      this.scrollLockObserver = new MutationObserver(syncBodyMargin)
      this.scrollLockObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-scroll-locked', 'style'],
      })
    },
    async accessChecks() {
      const accessService = new UserAccessService(types.user, types.company)
      // sales-dash-nav-panel fully rolled out — always use the platform side nav
      this.useSideNav = true
      await Promise.all([
        accessService.canAccess('sales-agent-in-ihm-dash'),
        accessService.canAccess('cm-fulltext-search-in-ihm-dash'),
      ])
        .then(([allowSalesAgent, allowPlatformSearch]) => {
          this.allowSalesAgent = allowSalesAgent
          this.allowPlatformSearch = allowPlatformSearch
        })
        .catch((err) => {
          this.$log.error('Error checking access:', err)
        })
    },
    async init() {
      // eslint-disable-next-line no-console
      console.warn('[Theme] App.init start', {
        hostname: window.location.hostname,
        isDev: this.$types.config.isDev(),
        isIHMLocation: this.$types.config.isIHMLocation(),
        __DEV__: window.__DEV__,
      })

      const c = this.$types.company.currentLoggedIn()
      const forceWhiteLabeled = this.$types.company.forceWhiteLabeled(c)
      const isIHMLocation = this.$types.config.isIHMLocation()
      const isNotDevMode = this.$types.config.isDev() === false

      if (forceWhiteLabeled && isIHMLocation && isNotDevMode) {
        // eslint-disable-next-line no-console
        console.warn('[Theme] App.init calling logout (white-label + IHM host + not __DEV__)')
        logout()
      }

      this.$log.debug(`Dev Mode: ${this.$types.config.isDev()}`)
      this.$log.debug(`Cache Disabled: ${this.$types.config.cacheDisabled()}`)
      this.$log.debug(`APIConfig`, this.$types.config.APIConfig())
      this.$log.debug(`UIConfig`, this.$types.config.UIConfig())
      this.$log.debug(`Sisense URL`, this.$types.config.SisenseURL())

      const theme = this.$types.theme.currentStyles()

      this.$vuetify.theme.dark = this.$types.theme.isDark()
      if (theme.dark) {
        this.$vuetify.theme.themes.dark = theme.dark.vuetify
      }
      if (theme.light) {
        this.$vuetify.theme.themes.light = theme.light.vuetify
      }

      // Apply theme early — before await loadLD/Pendo (those can hang or logout can leave before end)
      // Defaults: IHM → #ui-dashboards + .ihm; else → #ui-dashboards + .unified (owns portaled scopes too)
      this.$types.theme.currentTheme()
      this.$types.theme.applyResolvedTheme()

      const company = types.user.getCompany()
      const icon = types.company.forceWhiteLabeled(company) ? this.$types.company.getThemeIconUrl(company) : ''
      setFavicon(icon || `/branding/${window.location.hostname}/favicon.ico`)

      this.loadGlobalConfig()
      await this.loadLD()

      this.loadDashboards()
      await this.loadPendo()
      await this.loadNewRelic()
      window.addEventListener('beforeunload', () => {
        // close left navigation drawer when the page is unloaded
        this.$store.commit('toggleDrawer', null)
      })
    },
    getMailto(): string {
      const prefix = this.$types.company.isIHMBranded(false) ? 'iheartmedia-' : ''
      return `mailto:${prefix}support@unified.com`
    },
    dismissError(): void {
      this.$store.commit('broken', false)
    },
    reloadPage(): void {
      var url = new URL(window.location.href)
      url.searchParams.set('broken', 'true')
      window.location.href = url.toString()
    },
    isSuperBroken(): boolean {
      return this.$store.state.broken && !!this.$router.$queryParam('broken')
    },
    async loadDashboards() {
      try {
        this.$store.commit('dashboards', await this.$types.dashboard.loadAll())
      } catch (err) {
        this.$sendErr('An unexpected error occured loading your dashboards. Please refresh the page.')
        throw err
      }
    },
    loadGlobalConfig() {
      const jsId = 'unified-global-config'

      if (!document.getElementById(jsId)) {
        const src = `${this.$types.config.UPDomain()}/global.js`
        const scriptElm = document.createElement('script')
        scriptElm.setAttribute('src', src)
        scriptElm.setAttribute('id', jsId)

        document.head.appendChild(scriptElm)
      }
    },
    async loadLD() {
      const LD_KEY = this.$types.config.LaunchDarkly()

      if (!LD_KEY) {
        this.$log.error("Couldn't find the LD_CLIENT_ID")
        return
      }

      UserFlags.setLdClientSideId(LD_KEY)
      await this.loadLDFlags()
    },
    loadLDFlags() {
      return this.$store.dispatch('checkLDFlags', [
        'company-roll-up',
        'ihmcampaigns-selection',
        'new-initiative-dash',
        'violation-dashboard',
        'violation-dashboard-beta',
        'ui-deal-dashboard',
        'agency-leadership-dashboard',
        'bulk-optimizer-update-ui',
      ])
    },
    async loadPendo() {
      try {
        const api_key = this.$types.config.pendoAPIKey()

        if (!api_key) {
          return
        }
        await (function (apiKey) {
          ;(function (p: any, e, n, d: string, o: any) {
            var v: string[], w, x, y: any, z: any
            o = p[d] = p[d] || {}
            o._q = []
            v = ['initialize', 'identify', 'updateOptions', 'pageLoad']
            for (w = 0, x = v.length; w < x; ++w)
              (function (m) {
                o[m] =
                  o[m] ||
                  function () {
                    o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)))
                  }
              })(v[w])
            y = e.createElement(n)
            y.async = !0
            y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js'
            z = e.getElementsByTagName(n)[0]
            z.parentNode.insertBefore(y, z)
          })(window, document, 'script', 'pendo', {})
        })(api_key)

        window.pendo.initialize({
          visitor: {
            id: UserStorage.identityId(), // Required if user is logged in
            email: UserStorage.email(), // Recommended if using Pendo Feedback, or NPS Email
            full_name: UserStorage.firstName() + ' ' + UserStorage.lastName(),
            role: UserStorage.isAdmin(),
            title: UserStorage.title(),
            titleType: UserStorage.titleType(),
          },

          account: {
            id: UserStorage.parentCompany().id, // Highly recommended
            name: UserStorage.parentCompany().name,
            types: UserStorage.parentCompany().type,
            isCustomer: UserStorage.parentCompany().isCustomer,
          },
        })
      } catch (err) {
        throw err
      }
    },
    async loadNewRelic() {
      try {
        const nr = window.newrelic
        nr.setCustomAttribute('userEmail', UserStorage.email(), true)
      } catch (err) {
        throw err
      }
    },
    unimpersonate() {
      unimpersonate()
    },
  },
})
